import { Button, Row } from 'components';
import { DateRange } from 'components/DateRange';
import { cn } from 'domain/core';
import { useStationsPositions } from 'domain/hooks/api/api';
import {
	useObservations,
	useObservationsExcelDownload,
} from 'domain/hooks/api/observations';
import { useConvertData } from 'domain/hooks/useConvertData';
import { useDebounce } from 'domain/hooks/useDebounce';
import { VirtualStation } from 'domain/models/api.models';
import { ALL_VARIABLES } from 'domain/utils/variables';
import { ObservationGraphics } from 'pages/observations/ObservationGraphics';
import { MapMarkers } from 'pages/observations/ObservationSide';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const VirtualStationPreview = ({
	values,
}: {
	values: VirtualStation;
}) => {
	const debouncedValues = useDebounce(values, 300);

	const currentPositions = useStationsPositions(
		debouncedValues?.stations?.map((s) => s.id)
	);

	const today = new Date();
	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 14); // 14 days ago

	const [date, setDate] = useState({
		startDate: yesterday.toISOString().split('T')[0],
		endDate: today.toISOString().split('T')[0],
	});

	const [rawData, dataLoading] = useObservations(
		debouncedValues?.stations?.map((s) => s.id) ?? [],
		ALL_VARIABLES,
		undefined,
		undefined,
		date.startDate + 'T00:00:00.000000000Z',
		date.endDate + 'T23:59:59.000000000Z',
		'regular'
	);

	const excelDownload = useObservationsExcelDownload(
		debouncedValues?.stations?.map((s) => s.id) ?? [],
		ALL_VARIABLES,
		undefined,
		undefined,
		date.startDate + 'T00:00:00.000000000Z',
		date.endDate + 'T23:59:59.000000000Z',
		'descarga_observaciones'
	);

	const [data] = useConvertData(rawData);

	const { t } = useTranslation(['labels']);

	const [downloadLoading, setDownloadLoading] = useState(false);

	const handleDownload = async () => {
		try {
			setDownloadLoading(true);
			await excelDownload();
		} catch (e) {
			console.error(e);
		} finally {
			setDownloadLoading(false);
		}
	};

	return (
		<div
			className={cn('h-full w-full pt-8', {
				'opacity-50': dataLoading,
			})}
			key='map-markers'>
			<Row className='w-full justify-end'>
				<DateRange
					startDate={date.startDate}
					endDate={date.endDate}
					setEndDate={(d) =>
						setDate({
							...date,
							endDate: d,
						})
					}
					setStartDate={(d) =>
						setDate({
							...date,
							startDate: d,
						})
					}
				/>
				<Button size='sm' onClick={handleDownload} disabled={downloadLoading}>
					{t('labels:actions.download')}
				</Button>
			</Row>
			<ObservationGraphics
				data={data}
				loading={dataLoading}
				graphics={['tp', 'hvr']}
			/>

			<div className={cn('h-96 w-full')}>
				<MapMarkers
					zoom={{
						default: { max: 12 },
					}}
					marks={currentPositions}
				/>
			</div>
		</div>
	);
};
